import { useMemo, useEffect, memo, useState, useRef } from "react";
import { Box, Button, DialogContentText, Grid, TextField, Typography } from "@mui/material";
import { MobileDatePicker, MobileTimePicker } from "@mui/x-date-pickers";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import toast from "react-hot-toast";
import { Filter } from "bad-words";

import { DashboardContainer } from "src/layouts/components/content";
import { createPatientLog } from "src/services/home/homeService";
import { generateOTP } from "src/services/registeration/registerationService";
import DailogPopup from "src/layouts/components/dailog";

const filter = new Filter();

// ------------------- ComplaintForm Component -------------------
const ComplaintForm = memo( ({ onSubmit, isSubmitting }: { onSubmit: (data: any) => void; isSubmitting: boolean }) => {
  const schema = yup.object().shape({
    serviceType: yup
      .string()
      .required("Service type is required")
      .test("is-not-profane", "Profanity is not allowed", (val) => !filter.isProfane(val || "")),
    date: yup.date().required("Date is required").nullable(),
    time: yup.string().required("Time is required"),
    username: yup
      .string()
      .matches(/^[A-Za-z\s]+$/, "Only alphabets are allowed")
      .test("is-not-profane", "Profanity is not allowed", (val) => !filter.isProfane(val || "")),
    emailaddress: yup
      .string()
      .email("Invalid email")
      .matches(/^[^@]+@[^@]+\.[^@]+$/, "Invalid Email")
      .test("is-not-profane", "Profanity is not allowed", (val) => !filter.isProfane(val || "")),
    mobilenumber: yup
      .string()
       .matches(
    /^(?:\+61\d{9}|0\d{9})$/,
    'Mobile number must start with +61 or 0 followed by 9 digits'
  ),
    remark: yup
      .string()
      .required("Remark is required")
      .test("is-not-profane", "Profanity is not allowed", (val) => !filter.isProfane(val || "")),
  });


  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
const [otp, setOtp] = useState('');
const [pendingPayload, setPendingPayload] = useState<any>(null);
  const { control, handleSubmit, reset } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      serviceType: "",
      date: null,
      time: "",
      username: "",
      emailaddress: "",
      mobilenumber: "",
      remark: "",
    },
  });

  useEffect(() => {
    reset();
  }, [reset]);

  const RequiredLabel = (label: string) => (
    <span>
      {label} <span style={{ color: "red" }}>*</span>
    </span>
  );

  const getTimeAsDate = (seconds: number) => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setSeconds(seconds);
    return date;
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box maxWidth={800} mx="auto" px={3} py={3}>
        <Grid container spacing={3} direction="column">
          <Grid item xs={12}>
            <Controller
              name="serviceType"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={RequiredLabel("Service Type")}
                  size="small"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="username"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={RequiredLabel("Name")}
                  size="small"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="emailaddress"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={RequiredLabel("Email Address")}
                  size="small"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="mobilenumber"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={RequiredLabel("Mobile Number")}
                  size="small"
                  error={!!error}
                  helperText={error?.message}
                   value={
                field.value?.startsWith('+61')
                    ? field.value.replace(/^\+61(\d{3})(\d{3})(\d{3})$/, '+61 $1 $2 $3')
                    : field.value?.startsWith('0')
                        ? field.value.replace(/^0(\d{3})(\d{3})(\d{3})$/, '0$1 $2 $3')
                        : field.value || ''
            }
            onChange={(e) => {
                let input = e.target.value.replace(/[^\d+]/g, '');

                // Handle if starts with 0 or +61
                if (input.startsWith('0')) {
                    input = input.slice(0, 10); // 0 followed by 9 digits
                } else if (input.startsWith('+61')) {
                    input = input.slice(0, 12); // +61 followed by 9 digits
                }

                field.onChange(input);
            }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="date"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <MobileDatePicker
                  label={RequiredLabel("Service Date")}
                  value={field.value}
                  onChange={field.onChange}
                  disableFuture
                  format="dd/MM/yyyy"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      error: !!error,
                      helperText: error?.message,
                    },
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="time"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <MobileTimePicker
                  label={RequiredLabel("Service Time")}
                  onChange={(newValue) => {
                    if (newValue instanceof Date && !isNaN(newValue.getTime())) {
                      const seconds =
                        newValue.getHours() * 3600 + newValue.getMinutes() * 60;
                      field.onChange(seconds);
                    } else {
                      field.onChange(null);
                    }
                  }}
                  value={
                    typeof field.value === "number" ? getTimeAsDate(field.value) : null
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      error: !!error,
                      helperText: error?.message,
                    },
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="remark"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  rows={4}
                  size="small"
                  label={RequiredLabel("Remark")}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>
        </Grid>

        <Box mt={3}>
          <Typography variant="body2" color="textSecondary">
            Note: Fields marked with an asterisk (*) are required.
          </Typography>
        </Box>
        

        <Box mt={3} textAlign="center">
           <Button
  type="submit"
  variant="contained"
  color="primary"
  disabled={isSubmitting}
>
  {isSubmitting ? "Submitting..." : "Submit"}
</Button>


        </Box>
      </Box>
    </form>
  );
});


const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [otp, setOtp] = useState("");
  const [pendingPayload, setPendingPayload] = useState<any>(null);
  const [timer, setTimer] = useState(90);
      const [timerActive, setTimerActive] = useState(false);
          const [otpUuid, setOtpUuid] = useState('');
 const otpRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
         if (!open) {
             //setOtp('');
             setTimerActive(false);
         }
     }, [open])
 
     const restartTimer = () => {
         setTimer(90);
         setTimerActive(true);
     };
 
 
     useEffect(() => {
         let interval: NodeJS.Timeout;
 
         if (timerActive) {
             interval = setInterval(() => {
                 setTimer((prev) => {
                     if (prev <= 1) {
                         clearInterval(interval);
                         setTimerActive(false); // Stop the timer
                         return 0;
                     }
                     return prev - 1;
                 });
             }, 1000);
         }
 
         return () => clearInterval(interval);
     }, [timerActive]);
 
 
  const handleSubmitLog = async (data: any) => {
    try {
      setIsSubmitting(true);

      const formHash = JSON.stringify({
        serviceType: data.serviceType,
        username: data.username,
        emailaddress: data.emailaddress,
        mobilenumber: data.mobilenumber,
        date: data.date,
        time: data.time,
        remark: data.remark,
      });

      const lastSubmission = localStorage.getItem("lastComplaintSubmission");
      if (lastSubmission === formHash) {
        toast.error("You have already submitted this complaint.");
        setIsSubmitting(false);
        return;
      }

      const selectedDate = new Date(data.date);
      selectedDate.setHours(0, 0, 0, 0);
      const serviceDateTimestamp = Math.floor(selectedDate.getTime() / 1000);

      const fullDateWithTime = new Date(data.date);
      fullDateWithTime.setSeconds(data.time);
      const serviceTimeTimestamp = Math.floor(fullDateWithTime.getTime() / 1000);

      const payload = {
        serviceType: data.serviceType,
        complaintDate: serviceDateTimestamp,
        complaintTime: serviceTimeTimestamp,
        remark: data.remark,
        createdDate: Math.floor(Date.now() / 1000),
        username: data.username,
        emailaddress: data.emailaddress,
        mobilenumber: data.mobilenumber,
      };

      const otpRes = await generateOTP({
        mobileNumber: data.mobilenumber,
        dateOfBirth: new Date().toLocaleDateString("en-CA"),
        emailAddress: "",
        firstname: "",
        lastname: "",
        otpUuid: "",
      });

      if (otpRes.ok) {
        toast.success("OTP sent to your phone");
        setPendingPayload({ payload, formHash });
        setOtpDialogOpen(true);
            setOtpUuid(otpRes.data.otpUuid)
            restartTimer();
        setIsSubmitting(false); 
      } else {
        toast.error(otpRes.message || "Failed to send OTP");
        setIsSubmitting(false);
      }
    } catch (error) {
      toast.error("Failed to submit complaint");
      setIsSubmitting(false);
    }
  };

  const handleOtpVerifyAndSubmit = async () => {
  
  const otp = otpRef.current?.value?.trim();
    try {
      setIsSubmitting(true);
      const { payload, formHash } = pendingPayload;

      const finalPayload = {
        ...payload,
        otp,
        otpUuid
      };

      const res = await createPatientLog(finalPayload);

      if (res?.ok === false || res?.status >= 400) {
        toast.error(res?.message || "Failed to submit complaint");
        return;
      }

      localStorage.setItem("lastComplaintSubmission", formHash);
      toast.success("Complaint submitted successfully!");
      setOtpDialogOpen(false);
      setOtp("");

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err) {
      toast.error("Failed to verify OTP or submit complaint");
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleResendOtp = async () => {
  if (!pendingPayload?.payload?.mobilenumber) {
    toast.error("Missing mobile number for resending OTP.");
    return;
  }

  try {
    const mobileNumber = pendingPayload.payload.mobilenumber;

    const otpRes = await generateOTP({
      mobileNumber,
      dateOfBirth: new Date().toLocaleDateString("en-CA"),
      emailAddress: "",
      firstname: "",
      lastname: "",
      otpUuid: "",
    });

    if (otpRes.ok) {
      toast.success("OTP resent successfully.");
      setOtp("");
      restartTimer(); // restart the 90s timer
      if (otpRef.current) {
        otpRef.current.focus();
      }
    } else {
      toast.error(otpRes.message || "Failed to resend OTP.");
    }
  } catch (error) {
    console.error("Resend OTP error:", error);
    toast.error("An error occurred while resending OTP.");
  }
};


  return (
    <Box p={2}>
      <DashboardContainer
        title="Send us your Query"
        icon="mdi:account-alert-outline"
        isbutton={false}
      >

 
         <DailogPopup open={otpDialogOpen}
                onCancel={() => setOtpDialogOpen(false)}
                onSubmit={handleOtpVerifyAndSubmit}
                loading={isSubmitting}
                title={"Enter OTP"}
                disableSubmit={timer === 0}
                resendButton={
                    timer === 0 ? (
                        <Button
                            onClick={handleResendOtp}
                            variant="contained"
                            color="primary"
                        >
                            Resend OTP
                        </Button>
                    ) : null
                }
            >
                <DialogContentText>
                    Please enter the 4-digit OTP sent to your registered phone.
                </DialogContentText>
                <TextField
                    inputRef={otpRef}
                    autoFocus
                    margin="dense"
                    label="OTP"
                    type="text"
                    fullWidth
                    slotProps={{
                        inputLabel: {
                            shrink: true
                        }
                    }}
                    disabled={timer === 0}
                    inputProps={{
                        maxLength: 4,
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault(); // prevent default form submission
                            handleOtpVerifyAndSubmit(); // trigger OTP submission
                        }
                    }}
                />

                <Typography
                    variant="caption"
                    color={timer === 0 ? 'error' : 'textSecondary'}
                    sx={{ mt: 1 }}
                >
                    {timer > 0
                        ? `OTP expires in ${timer} second${timer === 1 ? '' : 's'}`
                        : 'OTP expired. Click "Resend OTP" to get a new one.'}
                </Typography>

            </DailogPopup>

 <ComplaintForm onSubmit={handleSubmitLog} isSubmitting={isSubmitting} />

      </DashboardContainer>
    </Box>
  );
};

export default Contact;
